import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { OptionProps } from 'react-select';

import type { IOption } from './types';
import { MOBILE_SELECT_VARIABLES } from './constants';

import { filterProps } from 'utils/helpers';

enum SOLUTIONS_SELECT_VARIABLES {
  color = '--color',
}

export enum SINGLE_VALUE_VARIABLES {
  textScale = '--text-scale',
}

const ValueContainer = styled.div`
  ${SOLUTIONS_SELECT_VARIABLES.color}: var(${MOBILE_SELECT_VARIABLES.activeTextColor});

  box-sizing: border-box;
  display: grid;
  grid-template-columns: min-content 1fr;
  column-gap: 2rem;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  padding: 1.5rem 1rem 1.5rem 2rem;
  width: 100%;
  user-select: none;
`;

const Icon = styled('div', filterProps('url'))<{
  url: string;
}>`
  ${({ url }) => css`
    background-color: var(${MOBILE_SELECT_VARIABLES.textColor});
    mask-image: url(${url});
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
    width: 3rem;
    height: 3rem;
  `}
`;

const Label = styled.span`
  ${SINGLE_VALUE_VARIABLES.textScale}: 1;

  margin: 0;
  overflow: hidden;
`;

const TextScale = styled.div`
  transform: scale(var(${SINGLE_VALUE_VARIABLES.textScale}));
  transform-origin: left;
`;

const Text = styled.span`
  ${({ theme: { tp } }) => css`
    line-height: calc(1.2 * var(${SINGLE_VALUE_VARIABLES.textScale}));
    white-space: nowrap;
    color: var(${MOBILE_SELECT_VARIABLES.textColor});
    font-size: 1.75rem;

    ${tp.useFont(fonts => fonts.Switzer.regular)};
  `}
`;

const useSingleValue = (label: string) => {
  const [resizeId, setResizeId] = useState(0);

  const labelRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const handleResize = () => setResizeId(id => id + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    if (textRef.current && labelRef.current) {
      const labelWidth = textRef.current.offsetWidth;
      const containerWidth = labelRef.current.offsetWidth;

      const textScale = labelWidth > containerWidth ? containerWidth / (labelWidth + 8) : 1;

      labelRef.current.setAttribute('style', `${SINGLE_VALUE_VARIABLES.textScale}: ${textScale};`);
    }
  }, [resizeId, label]);

  return { labelRef, textRef };
};

const SingleValue: React.FC<OptionProps<IOption, false>> = props => {
  const {
    innerRef,
    innerProps,
    data: { label, icon },
  } = props;

  const { labelRef, textRef } = useSingleValue(label);

  return (
    <ValueContainer ref={innerRef} {...innerProps}>
      {icon ? <Icon url={icon} /> : null}
      <Label ref={labelRef}>
        <TextScale>
          <Text ref={textRef}>{label}</Text>
        </TextScale>
      </Label>
    </ValueContainer>
  );
};

export default SingleValue;
